import { useEffect, useRef } from "react";

import type { ReactNode } from "react";
import { X } from "lucide-react";

import styles from "./SlideOver.module.scss";

// Hidden inputs are excluded: they match input:not([disabled]) but cannot take
// focus, so one sitting first or last in a panel would make focus() no-op after
// preventDefault and leave Tab doing nothing at that boundary.
//
// The guard is on the [disabled] attribute rather than :disabled, and that
// difference is load-bearing. A panel that turns read-only while it writes -
// which is what busy is for - usually does it with a disabled fieldset, and a
// fieldset's children are :disabled without carrying the attribute. Matching
// :disabled would empty this list for the whole write, and an empty list
// returns before preventDefault: Tab would then leave a panel still claiming
// aria-modal, at the one moment it exists to hold somebody inside.
const FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

// A panel over the page rather than a route, so the list behind it keeps its
// scroll position, its filters and its loaded pages. Design 1a uses the same
// shape for the prospect detail, so this is shared from the start rather than
// copied the second time something wants one.
//
// It claims aria-modal, so it has to earn it: focus moves in on open, Tab is
// trapped inside, and focus goes back where it came from on close. Without the
// trap the claim is a lie to assistive technology, and worse, a keyboard user
// can reach the page behind and act on it while a panel is over the top - which
// is how a form ends up saving one record's values onto another.
export function SlideOver({
  title,
  onClose,
  busy = false,
  children,
}: {
  title: string;
  onClose: () => void;
  // Set while a write the panel started is still in flight. A caller that
  // refuses to close at that moment has a reason - a mutation whose callbacks
  // die with the component - and this is what says so on screen, rather than
  // leaving a close button that looks live and does nothing.
  busy?: boolean;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);

  // Escape closes, and Tab is kept inside. Both are bound to the document
  // rather than the panel, and the Tab half has to be: when a focused button is
  // disabled - which is what the save button does while saving - the browser
  // blurs it to <body>, and a handler on the panel never sees the keydown that
  // follows. Focus would then leave for the page behind, on the one press the
  // trap exists for, while the panel still claims aria-modal.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        // Busy refuses this too, or the prop guards the two paths a pointer
        // takes and leaves the keyboard one open - which is the same unmount
        // mid-write, reached by the faster route.
        if (!busy) onClose();
        return;
      }

      if (event.key !== "Tab" || panel.current === null) return;

      const focusable = Array.from(
        panel.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focused = document.activeElement;

      // Focus has fallen outside - a disabled button, a removed field - so the
      // next Tab would land on the page behind. Pull it back rather than let
      // the panel become a suggestion.
      if (focused === null || !panel.current.contains(focused)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && focused === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && focused === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, busy]);

  // Focus into the first thing in the body - not the panel, whose first
  // focusable is the close button in the header. Landing there means the
  // operator's first Enter closes what they just opened.
  //
  // The opener is captured on mount and restored on unmount, or a keyboard user
  // is dropped at the top of the document and has to walk the whole list back
  // to where they were.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;

    const firstField = body.current?.querySelector<HTMLElement>(FOCUSABLE);
    (firstField ?? panel.current)?.focus();

    return () => opener?.focus();
  }, []);

  return (
    <>
      {/* The scrim closes on click but is not a button: it is decoration for a
          pointer, and Escape plus the close button are the accessible paths. */}
      <div
        className={styles.scrim}
        onClick={busy ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className={styles.panel}
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{title}</h2>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className={styles.body} ref={body}>
          {children}
        </div>
      </div>
    </>
  );
}
